<template>
    <div class="notification-center">
        <!-- 通知铃铛按钮 -->
        <el-popover
            placement="bottom"
            width="420"
            trigger="click"
            v-model="showPopover"
            popper-class="notification-popover"
            @show="onPopoverShow"
        >
            <div class="notification-panel">
                <!-- 连接状态提示 -->
                <div v-if="showConnectionWarning" class="connection-warning">
                    <i class="el-icon-warning"></i>
                    <span>{{ connectionStatusText }}</span>
                    <span class="attempts-text">（已尝试 {{ wsReconnectAttempts }} 次）</span>
                </div>

                <!-- 头部 -->
                <div class="panel-header">
                    <div class="title-with-status">
                        <span class="title">消息通知</span>
                        <span
                            class="connection-status"
                            :style="{ color: connectionStatusColor }"
                        >
                            <i class="status-dot" :style="{ backgroundColor: connectionStatusColor }"></i>
                            {{ wsState === 'connected' ? '实时' : '离线' }}
                        </span>
                    </div>
                </div>

                <!-- 类型筛选 -->
                <div class="type-filter">
                    <el-radio-group v-model="filterType" size="mini" @change="handleFilterChange">
                        <el-radio-button label="">全部</el-radio-button>
                        <el-radio-button label="import_progress">导入</el-radio-button>
                        <el-radio-button label="system">系统</el-radio-button>
                        <el-radio-button label="task">任务</el-radio-button>
                    </el-radio-group>
                </div>

                <!-- 通知列表 -->
                <div class="notification-list" v-loading="loading">
                    <template v-if="filteredNotifications.length > 0">
                        <div
                            v-for="(item,index) in filteredNotifications"
                            :key="index"
                            class="notification-item"
                            @click="handleItemClick(item)"
                        >
                            <!-- 类型图标 -->
                            <div :class="['item-icon', `type-${item.type}`]">
                                <i :class="getTypeIcon(item.type)"></i>
                            </div>

                            <!-- 内容 -->
                            <div class="item-content">
                                <div class="item-header">
                                    <span class="title-text">{{ item.title }}</span>
                                    <span class="type-tag">{{ getTypeLabel(item.type) }}</span>
                                </div>
                                <div class="item-message">{{ item.content || item.message }}</div>

                                <!-- 导入进度条 -->
                                <div v-if="item.type === 'import_progress' && item.progress !== undefined" class="progress-bar">
                                    <el-progress
                                        :percentage="item.progress"
                                        :status="item.progress >= 100 ? 'success' : undefined"
                                        :stroke-width="6"
                                    ></el-progress>
                                </div>

                                <div class="item-footer">
                                    <span class="item-time">{{ formatTime(item.created_at || item.create_time) }}</span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- 空状态 -->
                    <div v-else class="empty-state">
                        <i class="el-icon-bell"></i>
                        <p>暂无通知消息</p>
                    </div>
                </div>

                <!-- 底部 -->
                <div class="panel-footer" v-if="filteredNotifications.length > 0">
                    <span class="view-all-btn" @click="handleViewAll">查看全部消息</span>
                </div>
            </div>

            <!-- 触发按钮 -->
            <div slot="reference" class="notification-trigger">
                <div class="bell-wrapper">
                    <el-badge :value="unreadCount" :max="99" :hidden="unreadCount === 0" class="badge-item">
                        <i class="el-icon-bell"></i>
                    </el-badge>
                    <!-- 连接状态指示点 -->
                    <span
                        class="connection-indicator"
                        :class="wsState"
                        :style="{ backgroundColor: connectionStatusColor }"
                        :title="connectionStatusText"
                    ></span>
                </div>
            </div>
        </el-popover>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { NotificationTypeLabel } from '@/store/modules/main/notification'

export default {
    name: 'NotificationCenter',

    data() {
        return {
            showPopover: false,
            filterType: ''
        }
    },

    computed: {
        ...mapState('notification', ['notifications', 'loading']),
        ...mapGetters('notification', ['wsState', 'isWSConnected', 'isWSFailed', 'wsReconnectAttempts', 'unreadCount']),

        // 连接状态颜色
        connectionStatusColor() {
            const map = {
                connected: '#67c23a',      // 绿色
                connecting: '#909399',     // 灰色
                reconnecting: '#e6a23c',   // 黄色
                disconnected: '#909399',   // 灰色
                failed: '#f56c6c'          // 红色
            }
            return map[this.wsState] || '#909399'
        },

        // 连接状态文本
        connectionStatusText() {
            const map = {
                connected: '实时通知已连接',
                connecting: '连接中...',
                reconnecting: '重连中...',
                disconnected: '未连接',
                failed: '连接困难，持续重连中'
            }
            return map[this.wsState] || '未知状态'
        },

        // 是否显示连接警告
        showConnectionWarning() {
            return this.wsState === 'failed' || this.wsState === 'disconnected'
        },

        filteredNotifications() {
            if (!this.notifications || !Array.isArray(this.notifications)) {
                return []
            }
            if (!this.filterType) {
                return this.notifications.slice(0, 20)
            }
            return this.notifications
                .filter(n => n.type === this.filterType)
                .slice(0, 20)
        }
    },

    methods: {
        ...mapActions('notification', [
            'fetchNotifications',
            'fetchUnreadCount',
            'markAllAsRead'
        ]),

        // 获取类型图标
        getTypeIcon(type) {
            const icons = {
                'import_progress': 'el-icon-upload',
                'system': 'el-icon-setting',
                'task': 'el-icon-time'
            }
            return icons[type] || 'el-icon-message'
        },

        // 获取类型标签
        getTypeLabel(type) {
            return NotificationTypeLabel[type] || '通知'
        },

        // 格式化时间
        formatTime(timeStr) {
            if (!timeStr) return ''

            let time
            // 处理时间戳（秒或毫秒）
            if (typeof timeStr === 'number') {
                // 如果是秒级时间戳，转换为毫秒
                time = new Date(timeStr < 10000000000 ? timeStr * 1000 : timeStr)
            } else {
                time = new Date(timeStr)
            }

            // 检查日期是否有效
            if (isNaN(time.getTime()) || time.getFullYear() < 2000) {
                return ''
            }

            const now = new Date()
            const diff = now - time

            // 未来时间或负数
            if (diff < 0) {
                return '刚刚'
            }

            // 1分钟内
            if (diff < 60 * 1000) {
                return '刚刚'
            }
            // 1小时内
            if (diff < 60 * 60 * 1000) {
                return Math.floor(diff / (60 * 1000)) + '分钟前'
            }
            // 24小时内
            if (diff < 24 * 60 * 60 * 1000) {
                return Math.floor(diff / (60 * 60 * 1000)) + '小时前'
            }
            // 7天内
            if (diff < 7 * 24 * 60 * 60 * 1000) {
                return Math.floor(diff / (24 * 60 * 60 * 1000)) + '天前'
            }
            // 其他显示日期
            const year = time.getFullYear()
            const month = String(time.getMonth() + 1).padStart(2, '0')
            const day = String(time.getDate()).padStart(2, '0')
            const hour = String(time.getHours()).padStart(2, '0')
            const minute = String(time.getMinutes()).padStart(2, '0')

            if (year === now.getFullYear()) {
                return `${month}-${day} ${hour}:${minute}`
            }
            return `${year}-${month}-${day}`
        },

        // 弹窗显示时
        onPopoverShow() {
            this.fetchNotifications({ page: 1 })
            this.markAllAsRead()
        },

        // 筛选类型变更
        handleFilterChange() {
            // 本地筛选，不需要重新请求
        },

        // 点击通知项
        handleItemClick(item) {
            // 任务通知：跳转到任务大厅
            if (item.type === 'task') {
                this.showPopover = false
                this.$router.push('/uc_task_hall')
                return
            }

            // 导入进度：跳转到商品管理
            if (item.type === 'import_progress') {
                this.showPopover = false
                this.$router.push('/uc_goods')
                return
            }

            // 其他链接跳转
            if (item.link || item.url) {
                this.showPopover = false
                this.$router.push(item.link || item.url)
            }
        },

        // 查看全部
        handleViewAll() {
            this.showPopover = false
            this.$router.push('/notification')
        },

        // 刷新页面
        handleRefresh() {
            window.location.reload()
        }
    }
}
</script>

<style scoped>
.notification-center {
    display: inline-block;
}

.notification-trigger {
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 4px;
    transition: background-color 0.3s;
}

.notification-trigger:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

.notification-trigger .el-icon-bell {
    font-size: 20px;
    color: #fff;
}

.bell-wrapper {
    position: relative;
    display: inline-block;
}

.connection-indicator {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
}

.connection-indicator.reconnecting {
    animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.5;
        transform: scale(1.1);
    }
}

.notification-panel {
    margin: 0;
    background: #fff;
    border-radius: 4px;
    overflow: hidden;
}

.connection-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background-color: #fff8e6;
    border-bottom: 1px solid #ffd666;
    color: #d48806;
    font-size: 13px;
}

.connection-warning i {
    font-size: 16px;
}

.connection-warning .attempts-text {
    margin-left: 4px;
    font-size: 12px;
    opacity: 0.8;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid #ebeef5;
    background: #fff;
}

.title-with-status {
    display: flex;
    align-items: center;
    gap: 12px;
}

.panel-header .title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.connection-status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 400;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
}

.type-filter {
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
    background-color: #fafafa;
}

.notification-list {
    max-height: 420px;
    overflow-y: auto;
}

.notification-item {
    display: flex;
    align-items: flex-start;
    padding: 14px 16px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    position: relative;
    transition: background-color 0.3s;
}

.notification-item:last-child {
    border-bottom: none;
}

.notification-item:hover {
    background-color: #f5f7fa;
}

.item-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 12px;
}

.item-icon i {
    font-size: 18px;
    color: #fff;
}

.item-icon.type-import_progress {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.item-icon.type-system {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.item-icon.type-task {
    background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.item-content {
    flex: 1;
    min-width: 0;
    padding-right: 20px;
}

.item-header {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
}

.title-text {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
}

.type-tag {
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 10px;
    background-color: #f0f0f0;
    color: #909399;
    margin-left: 8px;
    flex-shrink: 0;
}

.item-message {
    font-size: 13px;
    color: #606266;
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin-bottom: 8px;
}

.progress-bar {
    margin-bottom: 8px;
}

.item-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.item-time {
    font-size: 12px;
    color: #909399;
}

.empty-state {
    padding: 50px 20px;
    text-align: center;
    color: #909399;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
    display: block;
}

.empty-state p {
    font-size: 14px;
    margin: 0;
}

.panel-footer {
    padding: 14px 16px;
    text-align: center;
    border-top: 1px solid #ebeef5;
}

.view-all-btn {
    font-size: 14px;
    color: #fa5757;
    cursor: pointer;
    transition: opacity 0.3s;
}

.view-all-btn:hover {
    opacity: 0.8;
}
</style>

<style>
/* 全局样式覆盖popover */
.notification-popover {
    padding: 0 !important;
    border: none !important;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1) !important;
}
.notification-popover .el-popover__title {
    display: none;
}
.notification-popover .el-popover__arrow {
    display: none;
}
</style>
